// ─────────────────────────────────────────────────────────────────────────────
// selection_recipes.ts — Selection-family recipes (P2)
//
// Each recipe is a pure function (match params + state ctx) → action list.
// The matcher sibling files decide which spell fires from a shape; the
// recipes here decide *what to do* once it fires.
//
// Recipes:
//   select.point     — tap on a block → set single-block selection
//   select.region    — lasso          → set selection to enclosed blocks
//   selection.erase  — line crossing strokes → erase strokes + cascade-
//                                                delete owning ink blocks
//   selection.clear  — meta-spell: drop the current selection
// ─────────────────────────────────────────────────────────────────────────────

import type { GrimoireAction } from '@shared/grimoire_action';
import type { SpellRecipe, RegisteredSpell } from '../recipe';

// ── select.point ────────────────────────────────────────────────────────────

interface SelectPointParams { blockId: string }

const selectPointRecipe: SpellRecipe<SelectPointParams> = (params, ctx) => [
  {
    type: 'selection.set',
    blockIds: [params.blockId],
    family: 'act',
    origin: ctx.origin,
    timestamp: ctx.now,
  } as GrimoireAction,
];

export const SELECT_POINT: RegisteredSpell<SelectPointParams> = {
  id: 'select.point',
  name: 'Tap select',
  family: 'act',
  discoverable: true,
  recipe: selectPointRecipe,
};

// ── select.region ───────────────────────────────────────────────────────────

interface SelectRegionParams { blockIds: string[] }

const selectRegionRecipe: SpellRecipe<SelectRegionParams> = (params, ctx) => {
  if (params.blockIds.length === 0) return [];   // matcher should prevent this
  return [
    {
      type: 'selection.set',
      blockIds: params.blockIds,
      family: 'act',
      origin: ctx.origin,
      timestamp: ctx.now,
    } as GrimoireAction,
  ];
};

export const SELECT_REGION: RegisteredSpell<SelectRegionParams> = {
  id: 'select.region',
  name: 'Lasso select',
  family: 'act',
  discoverable: true,
  recipe: selectRegionRecipe,
};

// ── selection.erase ─────────────────────────────────────────────────────────
// Erase the strokes the matcher identified (line-cross). For each
// stroke, also delete the owning ink block when known — cascade.

interface SelectionEraseParams { strokeIds: string[] }

const selectionEraseRecipe: SpellRecipe<SelectionEraseParams> = (params, ctx) => {
  const pageId = ctx.pageId;
  if (!pageId || params.strokeIds.length === 0) return [];
  const actions: GrimoireAction[] = [];

  // Erase strokes first so the block.delete cascade isn't masked by a
  // stroke that "rehydrates" the block on undo.
  for (const sid of params.strokeIds) {
    actions.push({
      type: 'ink.erase',
      pageId,
      target: sid,
      origin: ctx.origin,
      timestamp: ctx.now,
    });
  }

  // Cascade: any ink block whose strokes are all in the erase set gets
  // deleted too. The recipe queries ctx for ownership.
  const owningBlocks = ctx.findInkBlocksByStrokes(params.strokeIds);
  for (const bid of owningBlocks) {
    actions.push({
      type: 'block.delete',
      blockId: bid,
      pageId,
      origin: ctx.origin,
      timestamp: ctx.now,
    });
  }

  return actions;
};

export const SELECTION_ERASE: RegisteredSpell<SelectionEraseParams> = {
  id: 'selection.erase',
  name: 'Line erase',
  family: 'act',
  discoverable: true,
  recipe: selectionEraseRecipe,
};

// ── selection.clear ─────────────────────────────────────────────────────────
// Meta-spell — debug-only, no matcher.

interface SelectionClearParams { _empty?: never }

const selectionClearRecipe: SpellRecipe<SelectionClearParams> = (_p, ctx) => [
  { type: 'selection.clear', origin: ctx.origin, timestamp: ctx.now } as GrimoireAction,
];

export const SELECTION_CLEAR: RegisteredSpell<SelectionClearParams> = {
  id: 'selection.clear',
  name: 'Clear selection',
  family: 'act',
  discoverable: true,
  recipe: selectionClearRecipe,
};
